<template>
  <v-bottom-sheet
    v-model="internalVisible"
    class="order-summary-bottom-sheet"
    :persistent="false"
  >
    <v-card class="order-summary-bottom-sheet-card">
      <div
        class="order-summary-bottom-sheet-content"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div class="order-summary-bottom-sheet-handle"></div>

        <div v-if="locale === 'SG'" class="price-row">
          <span class="price-label">Total Activities</span>
          <span class="price-label">{{ totalActivities }} Activites</span>
        </div>

        <!-- Price Breakdown -->
        <div class="price-breakdown" v-if="locale === 'US'">
          <!-- Custom makercart subtotal -->
          <div v-if="hasBundledActivities" class="price-row">
            <span class="price-label">Custom makercart subtotal</span>
            <span class="price-value"
              >${{ formatPrice(total.percartTotal) }}</span
            >
          </div>

          <!-- Signature subtotal -->
          <div v-if="hasSignatureMakercarts" class="price-row">
            <span class="price-label">Signature subtotal</span>
            <span class="price-value"
              >${{ formatPrice(total.signaturePrice) }}</span
            >
          </div>

          <!-- Makercrate subtotal -->
          <div v-if="hasMakercrates" class="price-row">
            <span class="price-label">Makercrate subtotal</span>
            <span class="price-value"
              >${{ formatPrice(total.makercratePrice) }}</span
            >
          </div>

          <!-- Single activity subtotal -->
          <div v-if="hasSingleActivities" class="price-row">
            <span class="price-label">Single activity subtotal</span>
            <span class="price-value"
              >${{ formatPrice(total.singleSubtotal) }}</span
            >
          </div>

          <!-- Posters subtotal -->
          <div v-if="total.posterPrice > 0" class="price-row">
            <span class="price-label">Posters subtotal</span>
            <span class="price-value"
              >${{ formatPrice(total.posterPrice) }}</span
            >
          </div>

          <!-- Divider before total -->
          <div class="price-divider"></div>

          <!-- Total -->
          <div class="price-row total">
            <p class="price-label">
              <span class="price-label">{{
                isMakercrateOnlyCart ? "Total" : "Grand Total"
              }}</span>
              <span class="price-label-currency"> ({{ localeCurrency }})</span>
            </p>
            <span class="price-value bold"
              >${{ formatPrice(total.grandTotal) }}</span
            >
          </div>
        </div>

        <!-- Divider before form -->
        <div
          v-if="(showQuoteForm && !quoteGenerated) || locale === 'SG'"
          class="price-divider"
        ></div>

        <!-- Quote Form Section -->
        <transition name="form-fade">
          <div
            v-if="(showQuoteForm && !quoteGenerated) || locale === 'SG'"
            class="quote-form-section"
          >
            <div class="form-badge">
              <span class="badge-text"
                >Who should we address the quote to?</span
              >
            </div>

            <div class="form-field">
              <label class="form-label">
                Organization name<span class="required">*</span>
              </label>
              <input
                type="text"
                :value="quoteFormData.organization_name"
                @input="updateField('organization_name', $event.target.value)"
                :disabled="quoteGenerated || isRequestingQuote"
                placeholder="Fill the organization name"
                class="form-input"
              />
            </div>

            <div class="form-field">
              <label class="form-label">
                Name<span class="required">*</span>
              </label>
              <input
                type="text"
                :value="quoteFormData.attention_to"
                @input="updateField('attention_to', $event.target.value)"
                placeholder="Fill the name"
                :disabled="quoteGenerated || isRequestingQuote"
                class="form-input"
              />
            </div>

            <div class="form-field">
              <label class="form-label">
                City<span class="required">*</span>
              </label>
              <input
                type="text"
                :value="quoteFormData.city"
                @input="updateField('city', $event.target.value)"
                :disabled="quoteGenerated || isRequestingQuote"
                placeholder="Fill the city"
                class="form-input"
              />
            </div>

            <div class="form-field">
              <label class="form-label">
                State<span class="required">*</span>
              </label>
              <input
                type="text"
                :value="quoteFormData.state"
                @input="updateField('state', $event.target.value)"
                :disabled="quoteGenerated || isRequestingQuote"
                placeholder="Fill in the state"
                class="form-input"
              />
            </div>

            <div class="form-field">
              <label class="form-label">
                Address<span class="required">*</span>
              </label>
              <textarea
                :value="quoteFormData.address"
                @input="updateField('address', $event.target.value)"
                :disabled="quoteGenerated || isRequestingQuote"
                placeholder="Fill the address"
                class="form-textarea"
              ></textarea>
            </div>
          </div>
        </transition>

        <template v-if="locale === 'US'">
          <ActionButton
            v-if="!quoteGenerated && isMakercrateOnlyCart && !showQuoteForm"
            variant="primary"
            full-width
            :disabled="total.grandTotal <= 0"
            @click="$emit('checkout')"
          >
            <span>Checkout</span>
            <template #icon-right>
              <ShoppingCartIcon
                width="24"
                height="24"
                :color="total.grandTotal <= 0 ? '#6C737F' : '#ffff'"
              />
            </template>
          </ActionButton>

          <ActionButton
            v-if="!quoteGenerated"
            :variant="quoteButtonVariant"
            :disabled="
              total.grandTotal <= 0 ||
              (showQuoteForm && (!isFormValid || isRequestingQuote))
            "
            :loading="isRequestingQuote"
            @click="$emit('request-quote')"
          >
            <span>{{
              isMakercrateOnlyCart || showQuoteForm
                ? "Generate a Quote"
                : "Next"
            }}</span>
            <template #icon-right>
              <ChevronDownIcon
                v-if="!showQuoteForm && !isMakercrateOnlyCart"
                width="16"
                height="16"
                :color="
                  total.grandTotal <= 0 ||
                  (showQuoteForm && (!isFormValid || isRequestingQuote))
                    ? '#6C737F'
                    : quoteButtonVariant === 'secondary'
                      ? '#003d6a'
                      : '#ffff'
                "
              />
              <DocumentTextIcon
                v-else
                :color="
                  total.grandTotal <= 0 ||
                  (showQuoteForm && (!isFormValid || isRequestingQuote))
                    ? '#6C737F'
                    : quoteButtonVariant === 'secondary'
                      ? '#003d6a'
                      : '#ffff'
                "
                class="quote-icon"
              />
            </template>
          </ActionButton>

          <ActionButton
            v-if="isCrateQuoteFormOpen"
            variant="text"
            full-width
            :disabled="isRequestingQuote"
            @click="$emit('cancel-quote')"
          >
            <span>Cancel</span>
            <template #icon-right>
              <span class="cancel-x-icon">
                <img
                  src="/images/icon/cancel-x.svg"
                  alt=""
                  width="24"
                  height="24"
                />
              </span>
            </template>
          </ActionButton>

          <!-- Quote Generated Buttons -->
          <div v-if="quoteGenerated" class="quote-buttons">
            <ActionButton
              variant="primary"
              @click="$emit('download-quote')"
              class="download-quote-btn"
              :loading="isDownloadingQuote"
              loadingText="Downloading Quote..."
              :disabled="isDownloadingQuote"
            >
              <span>Download Quote</span>
            </ActionButton>
            <ActionButton
              variant="secondary"
              @click="$emit('resend-quote')"
              class="resend-quote-btn"
              :loading="isSendingQuote"
              :disabled="isSendingQuote"
            >
              <span>{{
                isSendingQuote ? "Sending..." : "Resend Quote via Email"
              }}</span>
            </ActionButton>
          </div>
        </template>
        <template v-else>
          <template v-if="!quoteGenerated">
            <div v-if="!hasFullActivityCart" class="tooltip-wrapper">
              <CustomTooltip placement="bottom">
                <template #content>
                  <p style="text-align: center">
                    A minimum of 8 activities in one Makercart are needed to
                    request a quote
                  </p>
                </template>
                <template #trigger>
                  <ActionButton
                    :disabled="true"
                    :loading="isRequestingQuote"
                    @click="$emit('request-quote')"
                    style="width: 100%"
                  >
                    <span>Request a Quote</span>
                    <template #icon-right>
                      <img
                        src="/images/icon/document-icon.svg"
                        alt="Quote Icon"
                        class="quote-icon"
                      />
                    </template>
                  </ActionButton>
                </template>
              </CustomTooltip>
            </div>
            <ActionButton
              v-else
              :disabled="!isFormValid"
              :loading="isRequestingQuote"
              @click="$emit('request-quote')"
            >
              <span>Request a Quote</span>
              <template #icon-right>
                <img
                  src="/images/icon/document-icon.svg"
                  alt="Quote Icon"
                  class="quote-icon"
                />
              </template>
            </ActionButton>
          </template>
        </template>
      </div>
    </v-card>
  </v-bottom-sheet>
</template>

<script setup>
import { computed, ref, watch, defineEmits, defineProps } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import {
  ChevronDownIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
} from "@/components/icons";
import { formatPrice } from "@/utils/index.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  total: {
    type: Object,
    required: true,
  },
  locale: {
    type: String,
    default: "US",
  },
  localeCurrency: {
    type: String,
    default: "USD",
  },
  showQuoteForm: {
    type: Boolean,
    default: false,
  },
  quoteGenerated: {
    type: Boolean,
    default: false,
  },
  quoteFormData: {
    type: Object,
    required: true,
  },
  isFormValid: {
    type: Boolean,
    default: false,
  },
  isRequestingQuote: {
    type: Boolean,
    default: false,
  },
  isDownloadingQuote: {
    type: Boolean,
    default: false,
  },
  isSendingQuote: {
    type: Boolean,
    default: false,
  },
  hasBundledActivities: {
    type: Boolean,
    default: false,
  },
  hasSignatureMakercarts: {
    type: Boolean,
    default: false,
  },
  hasSingleActivities: {
    type: Boolean,
    default: false,
  },
  hasMakercrates: {
    type: Boolean,
    default: false,
  },
  isMakercrateOnlyCart: {
    type: Boolean,
    default: false,
  },
  totalActivities: {
    type: Number,
    default: 0,
  },
  hasFullActivityCart: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "update:visible",
  "checkout",
  "request-quote",
  "cancel-quote",
  "download-quote",
  "resend-quote",
  "update:quoteFormData",
]);

const isCrateQuoteFormOpen = computed(
  () =>
    props.isMakercrateOnlyCart && props.showQuoteForm && !props.quoteGenerated
);

const quoteButtonVariant = computed(() =>
  props.isMakercrateOnlyCart && !props.showQuoteForm ? "secondary" : "primary"
);

const internalVisible = ref(props.visible);

const touchStartY = ref(0);
const touchStartTime = ref(0);
const touchStartScrollTop = ref(0);

const handleTouchStart = (e) => {
  const content = e.currentTarget;
  touchStartY.value = e.touches[0].clientY;
  touchStartTime.value = Date.now();
  touchStartScrollTop.value = content.scrollTop;
};

const handleTouchMove = (e) => {
  const content = e.currentTarget;
  const currentY = e.touches[0].clientY;
  const deltaY = currentY - touchStartY.value;

  if (
    touchStartScrollTop.value === 0 &&
    content.scrollTop === 0 &&
    deltaY > 0
  ) {
    e.preventDefault();
  }
};

const handleTouchEnd = (e) => {
  const content = e.currentTarget;
  const touchEndY = e.changedTouches[0].clientY;
  const touchEndTime = Date.now();
  const deltaY = touchEndY - touchStartY.value;
  const deltaTime = touchEndTime - touchStartTime.value;

  if (
    touchStartScrollTop.value === 0 &&
    content.scrollTop === 0 &&
    deltaY > 50 &&
    deltaTime < 300
  ) {
    internalVisible.value = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
  }
);

watch(internalVisible, (newVal) => {
  emit("update:visible", newVal);
});

const updateField = (field, value) => {
  emit("update:quoteFormData", { ...props.quoteFormData, [field]: value });
};
</script>

<style scoped>
@import "@/assets/css/colors.css";

.order-summary-bottom-sheet {
  z-index: 10000001;
}

.order-summary-bottom-sheet-card {
  border-radius: 28px 28px 0 0 !important;
  overflow: hidden;
  background-color: #fff;
}

.order-summary-bottom-sheet-handle {
  width: 40px;
  height: 4px;
  background-color: #d8dae5;
  border-radius: 2px;
  margin: 12px auto 0;
  flex-shrink: 0;
}

.order-summary-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px 24px;
  max-height: 80vh;
  overflow-y: auto;
}

.price-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  white-space: nowrap;
}

.price-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  padding: 0;
}

.price-label-currency {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-xs);
  color: var(--neutral-700);
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.048px;
}

.price-value {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 24px;
  color: #3b4648;
}

.price-value.bold {
  font-weight: 700;
}

.price-divider {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
}

.quote-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%)
    hue-rotate(0deg) brightness(100%) contrast(100%);
}

.quote-icon--secondary {
  filter: none;
}

.cancel-x-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.cancel-x-icon img {
  width: 24px;
  height: 24px;
  display: block;
}

.quote-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.download-quote-btn,
.resend-quote-btn {
  width: 100%;
  height: 48px;
}

.quote-form-section {
  background-color: #eaecff;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.form-badge {
  background-color: #4c4ddc;
  border-radius: 999px;
  padding: 4px 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  align-self: flex-start;
}

.badge-text {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--white);
  text-align: center;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.form-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
}

.required {
  color: #e00069;
}

.form-input {
  width: 100%;
  height: 40px;
  padding: 4px 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
  outline: none;
  transition: border-color 0.2s ease;
}

.form-input::placeholder {
  color: #6c737f;
  font-weight: 400;
}

.form-input:focus {
  border-color: var(--primary-blue);
}

.form-input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.form-textarea {
  width: 100%;
  height: 128px;
  padding: 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
  outline: none;
  resize: none;
  transition: border-color 0.2s ease;
}

.form-textarea::placeholder {
  color: #6c737f;
  font-weight: 500;
}

.form-textarea:focus {
  border-color: var(--primary-blue);
}

.form-textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
  color: #9ca3af;
  border-color: #e5e7eb;
}

.form-fade-enter-active,
.form-fade-leave-active {
  transition: all 0.3s ease;
}

.form-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.form-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

:deep(.tt-trigger) {
  width: 100%;
}

.tooltip-wrapper {
  width: 100%;
}

@media (max-width: 768px) {
  .order-summary-bottom-sheet-content {
    padding: 12px 16px 24px;
  }
}
</style>
